import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import BaseButton from "../Common/Buttons/BaseButton";
import PhoneInput from "../Common/Inputs/PhoneInput";

const AddContactsBody = ({
  contact,
  setContacts,
  setContact,
  contacts,
  editMode,
}) => {
  const [fieldDrafts, setFieldDrafts] = useState({});

  const handleAddContact = () => {
    if (!contact.trim()) return;

    const newContact = {
      phone: contact.trim(),
      customFields: [],
      showCustomFields: false,
    };

    setContacts((prev) => [...prev, newContact]);
    setContact("");
  };

  const handleDeleteContact = (index) => {
    setContacts((prev) => prev.filter((_, idx) => idx !== index));

    setFieldDrafts((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const handleContactChange = (index, newPhone) => {
    setContacts((prev) =>
      prev.map((contactData, idx) =>
        idx === index ? { ...contactData, phone: newPhone } : contactData
      )
    );
  };

  const handleAddField = (contactIndex) => {
    const draft = fieldDrafts[contactIndex] || { key: "", value: "" };
    if (!draft.key.trim() || !draft.value.trim()) return;

    setContacts((prev) =>
      prev.map((contactData, idx) =>
        idx === contactIndex
          ? {
              ...contactData,
              customFields: [
                ...contactData.customFields,
                { key: draft.key.trim(), value: draft.value.trim() },
              ],
            }
          : contactData
      )
    );

    setFieldDrafts((prev) => ({
      ...prev,
      [contactIndex]: { key: "", value: "" },
    }));
  };

  const handleDeleteField = (contactIndex, fieldIndex) => {
    setContacts((prev) =>
      prev.map((contactData, idx) =>
        idx === contactIndex
          ? {
              ...contactData,
              customFields: contactData.customFields.filter(
                (_, fIdx) => fIdx !== fieldIndex
              ),
            }
          : contactData
      )
    );
  };

  const handleFieldChange = (contactIndex, fieldIndex, type, newValue) => {
    setContacts((prev) =>
      prev.map((contactData, idx) =>
        idx === contactIndex
          ? {
              ...contactData,
              customFields: contactData.customFields.map((field, fIdx) =>
                fIdx === fieldIndex ? { ...field, [type]: newValue } : field
              ),
            }
          : contactData
      )
    );
  };

  const toggleCustomFields = (contactIndex) => {
    setContacts((prev) =>
      prev.map((contactData, idx) =>
        idx === contactIndex
          ? { ...contactData, showCustomFields: !contactData.showCustomFields }
          : contactData
      )
    );
  };

  return (
    <Box>
      <Box>
        <Typography className="font-14-grey mb-1">
          {editMode ? "Update" : "Add"} Contact
        </Typography>
        {!editMode && (
          <Box className="flex-align-center" gap={1} mb={3}>
            <PhoneInput
              value={contact}
              handleChange={(value) => setContact(value)}
            />

            <IconButton
              onClick={handleAddContact}
              sx={{
                background: "#fff",
                "&:hover": { background: "#f5f5f5" },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box>
        {[...contacts].reverse().map((contactData, contactIndex) => {
          const actualIndex = contacts.length - 1 - contactIndex;
          const draft = fieldDrafts[actualIndex] || { key: "", value: "" };

          return (
            <Card
              key={contactIndex}
              sx={{
                mb: 2,
                border: "1px solid rgba(204,210,222,0.6)",
                borderRadius: 2,
              }}
              elevation={0}
            >
              <CardContent sx={{ pb: 2 }}>
                <Box className="flex-align-center" gap={1}>
                  <Box width={"100%"}>
                    <Typography className="font-14-grey mb-1">Phone:</Typography>
                    <PhoneInput
                      value={contactData.phone}
                      handleChange={(value) =>
                        handleContactChange(actualIndex, value)
                      }
                    />
                  </Box>

                  {!editMode && (
                    <IconButton
                      onClick={() => handleDeleteContact(actualIndex)}
                      sx={{
                        color: "#d32f2f",
                        "&:hover": { background: "#ffebee" },
                        mt: 2,
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <BaseButton
                  title={
                    contactData.showCustomFields
                      ? "Hide Custom Fields"
                      : "Add Custom Fields"
                  }
                  variant="contained"
                  startIcon={<TuneIcon fontSize="small" />}
                  handleClick={() => toggleCustomFields(actualIndex)}
                  styles={{ my: 1, mb: 2, boxShadow: "none" }}
                />

                {contactData.showCustomFields && (
                  <Box>
                    <Typography className="font-14-grey mb-1">
                      Custom Fields
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mb={2}
                    >
                      <TextField
                        value={draft.key}
                        onChange={(e) =>
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [actualIndex]: {
                              ...prev[actualIndex],
                              key: e.target.value,
                              value: prev[actualIndex]?.value || "",
                            },
                          }))
                        }
                        sx={commonStyles.inputField}
                        placeholder="Field name (e.g., Name, Email)"
                        inputProps={{ maxLength: 30 }}
                      />
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        minWidth={10}
                      >
                        :
                      </Typography>
                      <TextField
                        value={draft.value}
                        onChange={(e) =>
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [actualIndex]: {
                              ...prev[actualIndex],
                              key: prev[actualIndex]?.key || "",
                              value: e.target.value,
                            },
                          }))
                        }
                        sx={commonStyles.inputField}
                        placeholder="Field value"
                        inputProps={{ maxLength: 30 }}
                      />
                      <IconButton
                        onClick={() => handleAddField(actualIndex)}
                        size="small"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    {contactData.customFields.map((field, fieldIndex) => (
                      <Box
                        className="flex-align-center mb-2"
                        gap={1}
                        key={fieldIndex}
                      >
                        <TextField
                          value={field.key}
                          onChange={(e) =>
                            handleFieldChange(
                              actualIndex,
                              fieldIndex,
                              "key",
                              e.target.value
                            )
                          }
                          sx={commonStyles.inputField}
                          placeholder="Field name"
                          inputProps={{ maxLength: 30 }}
                        />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          minWidth={10}
                        >
                          :
                        </Typography>
                        <TextField
                          value={field.value}
                          onChange={(e) =>
                            handleFieldChange(
                              actualIndex,
                              fieldIndex,
                              "value",
                              e.target.value
                            )
                          }
                          sx={commonStyles.inputField}
                          placeholder="Field value"
                          inputProps={{ maxLength: 30 }}
                        />
                        <IconButton
                          onClick={() =>
                            handleDeleteField(actualIndex, fieldIndex)
                          }
                          sx={{
                            color: "#d32f2f",
                            "&:hover": { background: "#ffebee" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default AddContactsBody;
